/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback, ReactNode } from 'react';
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
  Heading,
  Flex,
  Tabs,
  Stack,
  Spinner
} from '@chakra-ui/react';
import {
  DialogContent,
  DialogRoot,
  DialogTrigger,
  DialogCloseTrigger
} from '@/components/ui/dialog';
import {
  FileText,
  HeadCircuit,
  Info,
  LightbulbFilament,
  VideoConference,
  X
} from '@phosphor-icons/react';

import {
  AnalyzeTrialResumeResponse,
  apiAnalyzeTrialResume,
  apiGetTrialExtractorProviders
} from '@/api/services/trial.service';
import Resume from '@/components/data-display/Resume';
import logoSvg from '@/assets/icon/logo.svg';
import FileUploader from '@/views/Trial/FileUploader';
import ResumeAnalysisResult from '../Job/Candidate/ResumeAnalysisResult';
import { fetchAPI } from '@/api/fetch';
import CircleProgressBar from '@/components/data-display/CircularProgressBar';
import { CandidateOverviewSection } from '../Candidate/CandidateOverviewSection';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { PromptKey } from '@/types/enums';
import { LlmModelProvider, getLlmModelProviderSupportFileTypes } from '@/types/enums';
import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE } from '@/config/constants';

interface Provider {
  analysis_id: string | null;
  analysis_name: PromptKey;
  provider: LlmModelProvider | null;
  loading: boolean;
  error: string | null;
}

interface UploadState {
  resume_file: File | null;
  resume_file_error: string | null;
  jd_file: File | null;
  jd_file_error: string | null;
  uploading: boolean;
  error: string | null;
  loadingText?: string;
  result: AnalyzeTrialResumeResponse | null;
  first_name: string | null;
  last_name: string | null;
}

export default function TrialIndex() {
  const [uploadState, setUploadState] = useState<UploadState>({
    resume_file: null,
    first_name: null,
    last_name: null,
    resume_file_error: null,
    jd_file: null,
    jd_file_error: null,
    uploading: false,
    error: null,
    result: null
  });
  const [tab, setTab] = useState<string>('overview');
  const [provider, setProvider] = useState<Provider>({
    analysis_id: null,
    analysis_name: PromptKey.JDExtractor,
    provider: null,
    loading: true,
    error: null
  });

  const fileSizeLimit = MAX_RESUME_FILE_SIZE;

  const fetchProviders = useCallback(async () => {
    const defaultProvider: Provider = {
      analysis_id: null,
      analysis_name: PromptKey.JDExtractor,
      provider: null,
      loading: false,
      error: null
    };

    setProvider({ ...defaultProvider, loading: true, error: null });

    try {
      const response = await fetchAPI<AnalysisProvider[]>({
        request: apiGetTrialExtractorProviders
      });

      if (response.success) {
        const providersData = response.data;
        const extractorProvider = providersData.find(
          (p) => p.analysis_name === PromptKey.JDExtractor
        );

        if (extractorProvider) {
          setProvider({
            ...extractorProvider,
            loading: false,
            error: null
          });
        } else {
          console.error('No extractor provider configured.');
          setProvider({
            ...defaultProvider,
            error: 'No extractor provider configured. Please contact support to enable file upload.'
          });
        }
      } else {
        console.error('Error fetching analysis providers:', response.message);
        setProvider({
          ...defaultProvider,
          error: 'Failed to load file upload options. Please try again later.'
        });
      }
    } catch (error) {
      console.error('Unexpected error fetching analysis providers:', error);
      setProvider({
        ...defaultProvider,
        error: 'Failed to load file upload options. Please try again later.'
      });
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, []);

  const retryProviderFetch = useCallback(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (!uploadState.uploading) return;

    const baseMessages = ['Uploading', 'Processing', 'Extracting', 'Analyzing', 'Summarizing'];
    let index = 0;
    let dotCount = 0;
    const maxDots = 3;
    const maxRepeats = 4;
    let repeatCount = 0;

    const updateLoadingText = () => {
      setUploadState((prevState) => ({
        ...prevState,
        loadingText: `${baseMessages[index]}${'.'.repeat(dotCount)}`
      }));
      dotCount = (dotCount + 1) % (maxDots + 1);
      if (dotCount === 0 && ++repeatCount === maxRepeats) {
        index = (index + 1) % baseMessages.length;
        repeatCount = 0;
      }
    };

    const intervalId = setInterval(updateLoadingText, 500);
    updateLoadingText();

    return () => clearInterval(intervalId);
  }, [uploadState.uploading]);

  const handleResumeFileSelect = (file: File | null, error: string | null) => {
    if (file) {
      if (provider?.error || provider?.loading) {
        setUploadState((prev) => ({
          ...prev,
          resume_file_error: 'File upload is not available. Please wait or try again later.'
        }));
        return;
      }

      if (file.size > fileSizeLimit) {
        setUploadState((prev) => ({
          ...prev,
          resume_file_error: `File size exceeds ${getFileSize(fileSizeLimit)}`
        }));
        return;
      }

      setUploadState((prev) => ({
        ...prev,
        resume_file: file,
        resume_file_error: null,
        error: null
      }));
    } else {
      setUploadState((prev) => ({
        ...prev,
        resume_file_error: error || 'Invalid file type or size'
      }));
    }
  };

  const handleJdFileSelect = (file: File | null, error: string | null) => {
    if (file) {
      if (provider?.error || provider?.loading) {
        setUploadState((prev) => ({
          ...prev,
          jd_file_error: 'File upload is not available. Please wait or try again later.'
        }));
        return;
      }

      if (file.size > fileSizeLimit) {
        setUploadState((prev) => ({
          ...prev,
          jd_file_error: `File size exceeds ${getFileSize(fileSizeLimit)}`
        }));
        return;
      }

      setUploadState((prev) => ({
        ...prev,
        jd_file: file,
        jd_file_error: null,
        error: null
      }));
    } else {
      setUploadState((prev) => ({
        ...prev,
        jd_file_error: error || 'Invalid file type or size'
      }));
    }
  };

  const handleResumeFileRemove = () => {
    setUploadState((prev) => ({
      ...prev,
      resume_file: null,
      resume_file_error: null,
      error: null,
      result: null
    }));
  };

  const handleJdFileRemove = () => {
    setUploadState((prev) => ({
      ...prev,
      jd_file: null,
      jd_file_error: null,
      error: null
    }));
  };

  const handleUpload = async () => {
    if (!uploadState.resume_file) return;

    setUploadState((prev) => ({
      ...prev,
      uploading: true,
      error: null,
      resume_file_error: null,
      jd_file_error: null
    }));

    try {
      const response = await fetchAPI<AnalyzeTrialResumeResponse>({
        request: () =>
          apiAnalyzeTrialResume({
            payload: {
              resume_file: uploadState.resume_file!,
              jd_file: uploadState.jd_file || undefined
            }
          })
      });

      const data = response.data;
      const nameParts = data.resume_data.personal_information.name
        .split(' ')
        .filter((part) => part.trim() !== '');
      const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

      if (response.success) {
        setUploadState((prev) => ({
          ...prev,
          uploading: false,
          result: response.data,
          first_name: firstName,
          last_name: lastName,
          error: null
        }));
      } else {
        console.error('Failed to extract resume:', response.message);
        setUploadState((prev) => ({
          ...prev,
          uploading: false,
          error: 'Failed to extract resume. Please ensure the file is a valid resume format.'
        }));
      }
    } catch (error) {
      console.error('Error during resume extraction:', error);
      setUploadState((prev) => ({
        ...prev,
        uploading: false,
        error: 'Failed to extract resume. Please ensure the file is a valid resume format.'
      }));
    }
  };

  const reset = () => {
    setUploadState({
      resume_file: null,
      first_name: null,
      last_name: null,
      resume_file_error: null,
      jd_file: null,
      jd_file_error: null,
      uploading: false,
      error: null,
      result: null
    });

    setTab('overview');
  };

  return (
    <Flex
      bg="background.primary"
      minH="100vh"
      color="neutrals.900"
      px={{ base: '16px', md: '48px' }}
      flexDirection="column"
    >
      {/* Header */}
      <Box
        bg="background.primary"
        p={{ base: '16px 0', md: '24px 0' }}
        mb={{ base: '12px', md: '24px' }}
      >
        <Container>
          <Flex
            justify="space-between"
            align="center"
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: '12px', md: '0' }}
          >
            <Box
              cursor="pointer"
              w={{ base: '150px', md: '180px' }}
              onClick={reset}
              alignContent="center"
              alignItems="center"
            >
              <img src={logoSvg} alt="PolarisJobs" height="32px" />
            </Box>
            <Flex
              gap={{ base: '12px', md: '24px' }}
              align="center"
              direction={{ base: 'column', md: 'row' }}
            >
              <Button
                variant="outline"
                borderRadius="8px"
                borderColor="primary.900"
                px="20px"
                py="16px"
              >
                <a
                  href="https://go.polarisjobs.com/Demo2025"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Text
                    color={{ base: 'primary.900', md: 'primary.900' }}
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight={{ base: 'semibold', md: 'bold' }}
                  >
                    Full suite demo
                  </Text>
                </a>
              </Button>
              <a href="https://www.polarisjobs.com" target="_blank" rel="noopener noreferrer">
                <Text
                  cursor="pointer"
                  color={{ base: 'neutrals.700', md: 'primary.900' }}
                  fontSize={{ base: '14px', md: '16px' }}
                  fontWeight={{ base: 'semibold', md: 'bold' }}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Visit PolarisJobs
                </Text>
              </a>
            </Flex>
          </Flex>
        </Container>
      </Box>

      <VStack gap={{ base: '24px', md: '40px' }} align="stretch" flex="1">
        {/* Header */}
        <Box textAlign="center">
          <Heading size={{ base: 'xl', md: '2xl' }} mb={4} color="primary.900">
            Test-drive PolarisJobs AI
          </Heading>
          <Text
            fontSize={{ base: '16px', md: '20px' }}
            color="neutrals.700"
            maxW={{ base: '90%', md: '60%' }}
            mx="auto"
            textAlign={{ base: 'left', md: 'center' }}
          >
            See how our AI scores and analyzes a resume with no sign-up, no strings attached.
            Perfect for recruiters, get actionable insights in about 30 seconds.
          </Text>
          <Box maxW={{ base: '95%', md: '80%' }} mx="auto" mt="16px">
            <Text
              fontSize={{ base: '14px', md: '16px' }}
              color="neutrals.500"
              mb="8px"
              textAlign={{ base: 'left', md: 'center' }}
            >
              Upload a resume and, optionally, a JD. In seconds, you'll get:
            </Text>
            <VStack align="start" gap="4px" maxW="fit-content" color="neutrals.500" mx="auto">
              <Text
                fontSize={{ base: '14px', md: '16px' }}
                color="neutrals.600"
                display="flex"
                alignItems="center"
                gap="8px"
                textAlign={{ base: 'left', md: 'center' }}
              >
                <Box w="6px" h="6px" bg="primary.500" borderRadius="full" />
                Candidate overview and standardized resume format
              </Text>
              <Text
                fontSize={{ base: '14px', md: '16px' }}
                color="neutrals.600"
                display="flex"
                alignItems="center"
                gap="8px"
                textAlign={{ base: 'left', md: 'center' }}
              >
                <Box w="6px" h="6px" bg="primary.500" borderRadius="full" />
                Key strengths and skill gaps analysis (with JD)
              </Text>
              <Text
                fontSize={{ base: '14px', md: '16px' }}
                color="neutrals.600"
                display="flex"
                alignItems="center"
                gap="8px"
                textAlign={{ base: 'left', md: 'center' }}
              >
                <Box w="6px" h="6px" bg="primary.500" borderRadius="full" />
                Actionable interview recommendations (with JD)
              </Text>
            </VStack>
          </Box>
        </Box>

        {/* Upload Section */}
        {!uploadState.result && (
          <VStack mb={{ base: '24px', md: '40px' }}>
            {provider.error ? (
              <Box
                border="2px solid"
                borderRadius="12px"
                borderColor="error.300"
                bg="error.50"
                p="24px"
                textAlign="center"
              >
                <Text fontSize="18px" fontWeight="bold" color="error.700" mb="8px">
                  Upload Unavailable
                </Text>
                <Text fontSize="16px" color="error.600" mb="24px">
                  {provider.error}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={retryProviderFetch}
                  flexShrink={0}
                  color="neutrals.700"
                  fontSize="16px"
                  borderColor="neutrals.500"
                  borderRadius="8px"
                  p="8px 24px"
                >
                  Retry
                </Button>
              </Box>
            ) : provider.loading ? (
              <Box w="100%" textAlign="center">
                <Spinner size="lg" color="primary.900" mb="16px" />
                <Text fontSize="18px" fontWeight="bold" color="neutrals.700">
                  Loading...
                </Text>
                <Text fontSize="16px" color="neutrals.600">
                  Configuring file upload settings
                </Text>
              </Box>
            ) : (
              <Flex
                gap={{ base: '16px', md: '60px' }}
                direction={{ base: 'column', md: 'row' }}
                align="center"
                justify="center"
              >
                <FileUploader
                  id="resume-upload"
                  required
                  label="Resume"
                  file={uploadState.resume_file}
                  error={uploadState.resume_file_error}
                  uploading={uploadState.uploading}
                  supportedFileTypes={
                    provider?.provider
                      ? getLlmModelProviderSupportFileTypes(provider.provider)
                      : ['.pdf']
                  }
                  onFileSelect={handleResumeFileSelect}
                  onFileRemove={handleResumeFileRemove}
                />
                <FileUploader
                  id="jd-upload"
                  label="Job Description"
                  file={uploadState.jd_file}
                  error={uploadState.jd_file_error}
                  uploading={uploadState.uploading}
                  supportedFileTypes={
                    provider?.provider
                      ? getLlmModelProviderSupportFileTypes(provider.provider)
                      : ['.pdf']
                  }
                  onFileSelect={handleJdFileSelect}
                  onFileRemove={handleJdFileRemove}
                />
              </Flex>
            )}

            {uploadState.error && (
              <Text color="error.700" fontSize={{ base: '14px', md: '16px' }} role="alert">
                {uploadState.error}
              </Text>
            )}

            {uploadState.uploading && (
              <VStack align="center" gap="16px" mt="20px">
                <Text
                  fontSize={{ base: '18px', md: '20px' }}
                  color="neutrals.700"
                  fontWeight="bold"
                >
                  {uploadState.loadingText || 'Uploading...'}
                </Text>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={{ base: '90%', md: '60%' }}
                  fontSize={{ base: '14px', md: '16px' }}
                  color="neutrals.500"
                  textAlign="center"
                >
                  Our AI is hard at work unpacking, reading, and analyzing this resume. It usually
                  takes 30-60 seconds. Just enough time to sip your coffee and get ready for the
                  results.
                </Box>
              </VStack>
            )}

            {!provider.loading && !provider.error && (
              <>
                <Button
                  mt={{ base: '24px', md: '40px' }}
                  onClick={handleUpload}
                  disabled={!uploadState.resume_file || provider.loading}
                  loading={uploadState.uploading}
                  bg="primary.900"
                  color="neutrals.0"
                  size="lg"
                  px={{ base: '20px', md: '24px' }}
                  py={{ base: '20px', md: '24px' }}
                  borderRadius="8px"
                  fontSize={{ base: '14px', md: '16px' }}
                  _hover={{ bg: 'primary.700' }}
                  _disabled={{ bg: 'primary.700', color: 'neutrals.600' }}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  Analyze now
                </Button>
                <Text color="neutrals.500" fontSize={{ base: '12px', md: '14px' }}>
                  *Your uploaded files are not saved or stored
                </Text>
              </>
            )}
          </VStack>
        )}

        {/* Results Section */}
        {uploadState.result && (
          <VStack gap={{ base: '20px', md: '32px' }}>
            <Box
              w="full"
              border="1px solid"
              borderColor="neutrals.200"
              p={{ base: 4, md: 6 }}
              borderRadius="12px"
            >
              <Flex justify="space-between" gap={{ base: '12px', sm: '0' }}>
                <Flex color="neutrals.700" alignItems="center" gap={4} maxW="90%">
                  <Box w={{ base: '24px', md: '32px' }} h={{ base: '24px', md: '32px' }}>
                    <FileText />
                  </Box>
                  <Text
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight="bold"
                    color="neutrals.700"
                  >
                    {uploadState.resume_file?.name}
                  </Text>
                </Flex>
                <Button
                  onClick={() => {
                    reset();
                  }}
                  variant="ghost"
                  size="sm"
                  aria-label="Clear results"
                  color="error.500"
                >
                  <X weight="bold" size={24} />
                </Button>
              </Flex>
              {uploadState.jd_file && (
                <Flex color="neutrals.700" alignItems="center" gap={4} mt="12px">
                  <Box w={{ base: '24px', md: '32px' }} h={{ base: '24px', md: '32px' }}>
                    <FileText />
                  </Box>
                  <Text
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight="bold"
                    color="neutrals.700"
                  >
                    {uploadState.jd_file.name}
                  </Text>
                </Flex>
              )}
            </Box>

            <Flex w="100%" flexDirection="column" gap={4}>
              <Tabs.Root
                defaultValue="overview"
                variant="outline"
                value={tab}
                onValueChange={(e) => setTab(e.value)}
              >
                <Tabs.List>
                  <Tabs.Trigger
                    value="overview"
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight={tab === 'overview' ? 'bold' : 'normal'}
                    bg={tab === 'overview' ? 'background.secondary' : 'transparent'}
                    px={{ base: '12px', md: '16px' }}
                    py={{ base: '8px', md: '12px' }}
                  >
                    <Info size={20} />
                    <Box display={{ base: tab === 'overview' ? 'block' : 'none', sm: 'block' }}>
                      Overview
                    </Box>
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="resume"
                    fontSize={{ base: '14px', md: '16px' }}
                    fontWeight={tab === 'resume' ? 'bold' : 'normal'}
                    px={{ base: '12px', md: '16px' }}
                    py={{ base: '8px', md: '12px' }}
                  >
                    <FileText size={20} />
                    <Box display={{ base: tab === 'resume' ? 'block' : 'none', sm: 'block' }}>
                      Resume
                    </Box>
                  </Tabs.Trigger>
                  {uploadState.result.analysis_result && (
                    <Tabs.Trigger
                      value="analysis"
                      fontSize={{ base: '14px', md: '16px' }}
                      fontWeight={tab === 'analysis' ? 'bold' : 'normal'}
                      bg={tab === 'analysis' ? 'background.secondary' : 'transparent'}
                      px={{ base: '12px', md: '16px' }}
                      py={{ base: '8px', md: '12px' }}
                    >
                      <LightbulbFilament size={20} />
                      <Box display={{ base: tab === 'analysis' ? 'block' : 'none', sm: 'block' }}>
                        Analysis
                      </Box>
                    </Tabs.Trigger>
                  )}
                  <PremiumFeatureDialog
                    icon={<VideoConference size={20} />}
                    featureName="Interview Copilot"
                  />
                  <PremiumFeatureDialog
                    icon={<HeadCircuit size={20} />}
                    featureName="Screening Agent"
                  />
                </Tabs.List>
                <Tabs.Content
                  value="overview"
                  bg="background.secondary"
                  p={{ base: '24px 16px 60px 16px', md: '60px 120px' }}
                >
                  <CandidateOverviewSection
                    firstName={uploadState.first_name || ''}
                    lastName={uploadState.last_name || ''}
                    email={uploadState.result.resume_data.personal_information.email}
                    phone={uploadState.result.resume_data.personal_information.phone}
                    linkedin={uploadState.result.resume_data.personal_information.linkedin}
                    fitPositions={uploadState.result.fit_job_positions}
                    resume={uploadState.result.resume_data}
                  />
                  <CTA onReset={reset} />
                </Tabs.Content>
                <Tabs.Content value="resume" mb={{ base: '60px', md: '80px' }}>
                  <Flex justify="center" align="center" direction="column" w="100%">
                    <Box w={{ base: '95%', md: '80%' }} mt={{ base: '24px', md: '48px' }}>
                      <Resume resume={uploadState.result.resume_data} />
                    </Box>
                    <CTA onReset={reset} />
                  </Flex>
                </Tabs.Content>
                {uploadState.result.analysis_result && (
                  <Tabs.Content
                    value="analysis"
                    bg="background.secondary"
                    pb={{ base: '60px', md: '60px' }}
                  >
                    <Flex
                      justify="center"
                      align="center"
                      w="100%"
                      pt={{ base: '24px', md: '48px' }}
                      px={{ base: '16px', md: '0' }}
                    >
                      <Box w={{ base: '100%', md: '80%' }}>
                        <Box
                          mb="32px"
                          w={{ base: '80px', md: '100px' }}
                          mx={{ base: 'auto', md: '0' }}
                        >
                          <CircleProgressBar
                            value={uploadState.result.analysis_result.fit_score.score}
                          />
                        </Box>
                        <ResumeAnalysisResult
                          analysisResult={uploadState.result.analysis_result}
                          followUpQuestions={uploadState.result.follow_up_items}
                        />
                      </Box>
                    </Flex>
                    <CTA onReset={reset} />
                  </Tabs.Content>
                )}
              </Tabs.Root>
            </Flex>
          </VStack>
        )}
      </VStack>

      {/* Footer */}
      <Box as="footer" bg="background.primary" h="40px" alignContent="center">
        <Text fontSize="14px" color="neutrals.700" textAlign="center">
          © 2025 PolarisJobs. All rights reserved.
        </Text>
      </Box>
    </Flex>
  );
}

interface PremiumFeatureDialogProps {
  icon: ReactNode;
  featureName: string;
}

const PremiumFeatureDialog = ({ icon, featureName }: PremiumFeatureDialogProps) => {
  return (
    <DialogRoot>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          _hover={{ bg: 'transparent' }}
          fontSize={{ base: '14px', md: '16px' }}
          color="neutrals.700"
          px={{ base: '12px', md: '16px' }}
          py={{ base: '8px', md: '12px' }}
        >
          <Flex align="center" gap={2}>
            {icon}
            <Box display={{ base: 'none', sm: 'block' }}>{featureName}</Box>
          </Flex>
        </Button>
      </DialogTrigger>
      <DialogContent borderRadius="8px" maxW="500px" w="90%" bg="neutrals.0">
        <DialogCloseTrigger position="absolute" top="16px" right="16px" aria-label="Close dialog" />
        <VStack gap="24px" p={{ base: '20px', md: '24px' }} textAlign="center">
          <Heading size={{ base: 'md', md: 'lg' }} color="primary.900">
            Premium Only
          </Heading>
          <Text
            fontSize={{ base: '14px', md: '16px' }}
            color="neutrals.700"
            maxW={{ base: '100%', md: '80%' }}
            mx="auto"
            lineHeight="1.5"
          >
            To unlock{' '}
            <Text as="span" color="primary.900" fontWeight="bold">
              {featureName}
            </Text>{' '}
            and other premium features, get in touch with us.
          </Text>
          <Button
            bg="primary.900"
            color="neutrals.0"
            size={{ base: 'md', md: 'lg' }}
            px={{ base: '20px', md: '24px' }}
            py={{ base: '10px', md: '12px' }}
            borderRadius="8px"
            fontSize={{ base: '14px', md: '16px' }}
            _hover={{ bg: 'primary.700' }}
          >
            <a
              href="https://www.polarisjobs.com/schedule-demo"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a Demo
            </a>
          </Button>
        </VStack>
      </DialogContent>
    </DialogRoot>
  );
};

const CTA = ({ onReset }: { onReset: () => void }) => {
  return (
    <Stack align="center" gap="16px" mt={{ base: '40px', md: '60px' }} w="100%">
      <Text
        w={{ base: '100%', md: '80%' }}
        fontSize={{ base: '16px', md: '18px' }}
        color="neutrals.700"
        fontWeight="bold"
        textAlign="center"
      >
        Get on a quick call with our team to learn more about our product, or try our AI analysis
        with another resume.
      </Text>
      <Flex
        gap="16px"
        justify="center"
        wrap="wrap"
        direction={{ base: 'column', sm: 'row' }}
        w={{ base: '80%', sm: 'auto' }}
      >
        <Button
          bg="primary.900"
          color="neutrals.0"
          size="lg"
          px={{ base: '20px', md: '24px' }}
          py={{ base: '20px', md: '24px' }}
          borderRadius="8px"
          fontSize={{ base: '14px', md: '16px' }}
          _hover={{ bg: 'primary.700' }}
          w={{ base: '100%', sm: 'auto' }}
        >
          <a
            href="https://www.polarisjobs.com/schedule-demo"
            target="_blank"
            rel="noopener noreferrer"
          >
            Book a call
          </a>
        </Button>
        <Button
          bg="neutrals.0"
          color="primary.900"
          border="1px solid"
          borderColor="primary.900"
          size="lg"
          px={{ base: '20px', md: '24px' }}
          py={{ base: '20px', md: '24px' }}
          borderRadius="8px"
          fontSize={{ base: '14px', md: '16px' }}
          w={{ base: '100%', sm: 'fit-content' }}
          _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
          onClick={onReset}
        >
          Analyze another resume
        </Button>
      </Flex>
    </Stack>
  );
};
